import { z } from 'zod'
import { BILLING } from '../constants/billing.js'
import { pageCursorSchema } from '../helpers/page-cursor.js'
import { uuidSchema } from '../validators/params.js'

/** A page of billing history, newest first. */
export const billingHistoryQuerySchema = z
  .object({
    /** A previous page's `nextBefore`. */
    before: pageCursorSchema.optional(),
  })
  .strict()

export type BillingHistoryQuery = z.infer<typeof billingHistoryQuerySchema>

/** An Owner buys credit: $20.00 to $1,000.00 before tax. */
export const createPurchaseInputSchema = z
  .object({
    amountCents: z.number().int().min(BILLING.purchaseMinCents).max(BILLING.purchaseMaxCents),
  })
  .strict()

export type CreatePurchaseInput = z.infer<typeof createPurchaseInputSchema>

/** An Owner refunds unused paid credit, less the fee. */
export const createRefundInputSchema = z
  .object({
    /** Credit to take off the balance, at most the paid credit not yet spent. */
    amountCents: z.number().int().min(1),
    /** Repeat the same id to retry the same refund: it's made once. */
    requestId: uuidSchema,
  })
  .strict()

export type CreateRefundInput = z.infer<typeof createRefundInputSchema>

/**
 * Auto-reload on: when a live send leaves the balance below the trigger, the
 * saved card is charged the amount (plus tax), at most the monthly cap per
 * calendar month in the organization's timezone.
 */
export const autoReloadOnSchema = z
  .object({
    enabled: z.literal(true),
    triggerCents: z.number().int().min(BILLING.autoReloadTriggerMinCents).max(BILLING.thresholdMaxCents),
    amountCents: z.number().int().min(BILLING.purchaseMinCents).max(BILLING.purchaseMaxCents),
    monthlyCapCents: z.number().int().min(BILLING.purchaseMinCents).max(BILLING.autoReloadMonthlyCapMaxCents),
  })
  .strict()
  .refine((value) => value.monthlyCapCents >= value.amountCents, {
    message: 'The monthly cap must be at least the reload amount',
    path: ['monthlyCapCents'],
  })

export type AutoReloadOn = z.infer<typeof autoReloadOnSchema>

/** An Owner turns auto-reload on (with its numbers) or off. */
export const setAutoReloadInputSchema = z.union([autoReloadOnSchema, z.object({ enabled: z.literal(false) }).strict()])

export type SetAutoReloadInput = z.infer<typeof setAutoReloadInputSchema>

/** An Owner saves a card on Stripe's page; with `autoReload`, those settings turn on once it's saved. */
export const createCardSetupInputSchema = z.object({ autoReload: autoReloadOnSchema.optional() }).strict()

export type CreateCardSetupInput = z.infer<typeof createCardSetupInputSchema>

/** Staff ADMIN adds credit to an organization: a pilot, or a correction. */
export const grantCreditInputSchema = z
  .object({
    amountCents: z.number().int().min(1).max(BILLING.grantMaxCents),
    /** Owners see this, in an email and in their history. */
    reason: z.string().trim().min(1).max(500),
    /** Repeat the same id to retry the same grant: it's made once. */
    requestId: uuidSchema,
  })
  .strict()

export type GrantCreditInput = z.infer<typeof grantCreditInputSchema>

/** An Owner sets when the low-balance alert fires: $0.00 (never) to $1,000.00. */
export const setLowBalanceThresholdInputSchema = z
  .object({
    thresholdCents: z.number().int().min(0).max(BILLING.thresholdMaxCents),
  })
  .strict()

export type SetLowBalanceThresholdInput = z.infer<typeof setLowBalanceThresholdInputSchema>

/**
 * Staff ADMIN schedules a new price per live envelope. The schedule is
 * append-only: it must take effect after every change already scheduled, and
 * an increase at least 30 days out. Omit effectiveAt for now (a decrease).
 */
export const schedulePriceInputSchema = z
  .object({
    priceMicros: z.number().int().min(1).max(BILLING.priceMaxMicros),
    effectiveAt: z.string().datetime().optional(),
  })
  .strict()

export type SchedulePriceInput = z.infer<typeof schedulePriceInputSchema>

export const setExemptionInputSchema = z.object({ exempt: z.boolean() }).strict()

export type SetExemptionInput = z.infer<typeof setExemptionInputSchema>
